<script setup lang="ts">
import type { Editor, JSONContent } from "@tiptap/vue-3"

import Document from "@tiptap/extension-document"
import Paragraph from "@tiptap/extension-paragraph"
import Placeholder from "@tiptap/extension-placeholder"
import Text from "@tiptap/extension-text"
import { EditorContent, useEditor } from "@tiptap/vue-3"
import { uuidv7 } from "uuidv7"

import { PropertySchema } from "~/classes/entity"
import { EditableTag } from "~/classes/file/properties/EditableTag"
import { StaticFile } from "~/classes/file/StaticFile"

const props = defineProps({
  file: null,
  focusOnMount: { type: Boolean }
})
const emit = defineEmits(["blur"])

const console = useLogger("TagEditor", theme.colors.red.hex)
const editor = useEditor({
  content: fileTagsAsContents(props.file),
  extensions: [
    Document,
    Paragraph,
    Text,
    Placeholder,
    EditableTag.createNode(),
  ],
  editorProps: {
    handleKeyDown: (_, event) => onKeyDown(editor.value!, event),
  },
  onUpdate: () => onUpdated(),
  onBlur: () => emit("blur"),
})
const { state: myself } = useSelf()
const { notifyError } = useAlerts()

/**
 * Tags have the following validations:
 * - Must be lower case (this prevents annoying non-collisions)
 * - Must not be empty
 * - Must not contain any control characters or the following characters:
 *   <>"'{}`#$^
 * - Must not contain untrimmed whitespace
 * - Must not exceed 64 characters
 * - Must not contain a colon unless it is a color tag
 */
function onKeyDown(editor: Editor, event: KeyboardEvent) {
  if (event.key === "Enter") {
    const cursor = editor.state.selection.$from
    const from = cursor.start()
    const to = cursor.end()
    const value = cursor.node().textContent.trim().toLowerCase().slice(0, 64)
    if (!StaticFile.isTagValid(value)) {
      event.preventDefault()
      editor
        .chain()
        .deleteRange({ from, to })
        .focus()
        .run()
      notifyError(
        "Invalid tag",
        "Tags must be unique, contain lowercase letters, not contain the following characters: <>'\"{}#^$` and have a valid prefix, if any.",
      )
      return true
    }

    editor
      .chain()
      .deleteRange({ from, to })
      .insertContentAt(from, [
        { type: EditableTag.TAG, attrs: { value } },
        { type: "text", text: " " }, // must have space otherwise tiptap can't create it
      ])
      .focus()
      .run()
    return true
  }
  // prevent user from entering invalid characters
  else if (!event.ctrlKey && !event.metaKey) {
    // Check for invalid characters: control chars or <>"'{}`#$^
    if (/[<>"'{}`#$^]/.test(event.key)) {
      event.preventDefault()
      return true
    }
    else if (/^[A-Z]$/.test(event.key)) {
      event.preventDefault()
      editor.chain().insertContent(event.key.toLowerCase()).run()
      return true
    }
    else if (event.key === ":") {
      const value = editor.state.selection.$from.node().textContent.trim()
      if (!StaticFile.isTagValid(`${value}:`)) {
        event.preventDefault()
        return true
      }
    }
    return false
  }
  return false
}

/** This editor's current tags */
function tags(): Set<string> {
  if (!editor.value)
    throw new Error("No editor value")
  const editorJson = editor.value?.getJSON()
  const tags = new Set<string>()
  for (const node of editorJson.content || []) {
    if (node.type !== EditableTag.TAG)
      continue
    const text = node.attrs?.value
    if (!text)
      continue
    tags.add(text)
  }
  return tags
}

/** When the editor text is updated */
async function onUpdated() {
  if (!editor.value)
    throw new Error("No editor value")
  if (!myself.value.user)
    throw new Error("Not logged in")
  const tagsToAdd = tags()
  const tagsToRemove = props.file.tags()
  for (const tag of tagsToAdd.intersection(tagsToRemove)) {
    tagsToAdd.delete(tag)
    tagsToRemove.delete(tag)
  }

  const timestamp = uuidv7()
  const updates = []
  for (const tag of tagsToAdd) {
    updates.push({
      propertyId: await StaticFile.tagPropertyId(tag),
      newVal: { type: PropertySchema.HashableTagValue, hashableTag: tag } as EntityPropertyValue,
      deleted: false,
      unversioned: true,
      timestamp,
    })
  }
  for (const tag of tagsToRemove) {
    updates.push({
      propertyId: await StaticFile.tagPropertyId(tag),
      newVal: { type: PropertySchema.HashableTagValue, hashableTag: tag } as EntityPropertyValue,
      deleted: true,
      unversioned: true,
      timestamp,
    })
  }

  if (len(updates)) {
    props.file.updateProperties(updates)
    console.debug("Updated tags", tagsToAdd.values(), tagsToRemove.values())
  }
}

function focusOnTagEditor() {
  editor.value?.commands.focus("end")
}

function fileTagsAsContents(file: StaticFile): JSONContent {
  const content: JSONContent[] = []

  // we sort tags by oldest timestamp first so that you don't see jumping in editor
  const tags = file
    .tagProperties()
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    .map(p => p.value.type === PropertySchema.HashableTagValue ? p.value.hashableTag : undefined)
    .filter(tag => tag)
    .map(tag => tag!)

  for (const value of tags) {
    content.push({
      type: EditableTag.TAG,
      attrs: { value },
    })
    content.push({
      type: "paragraph",
      content: [],
    })
  }
  return {
    type: "doc",
    content,
  }
}

/** Computes whether the tags are different then reloads the editor if so */
function reloadTagEditor(file: StaticFile, oldFile?: StaticFile) {
  if (file.id === oldFile?.id) {
    const tags = file.tags()
    const oldTags = oldFile.tags()
    if ((tags.size === oldTags.size) && tags.isSubsetOf(oldTags))
      return
  }
  editor.value?.chain().setContent(fileTagsAsContents(file)).run()
}

function blurEditor() {
  editor.value?.chain().setTextSelection(0).blur().run()
}

watch(() => props.file, reloadTagEditor)
onMounted(() => {
  // The timeout here should ideally match the transition duration for the
  // search bar
  if (props.focusOnMount)
    setTimeout(focusOnTagEditor, 100)
  else
    blurEditor()
})
</script>

<template>
  <div class="tag-editor">
    <editor-content :editor class="editor-content" />
  </div>
</template>

<style lang="sass">
@import '~/assets/styles/generated/variables.sass'

.tag-editor
  .tiptap
    @apply flex flex-wrap gap-0.5 items-center p-0.5
    &.ProseMirror-focused
      outline: none
    p.is-editor-empty:first-child::before
        @apply text-[#{$global-t-quaternary}] font-normal float-left h-0 pointer-events-none
        @apply content-['Add_tags_(enter_to_save)...']
    // this is needed for the end of the tags list to grow to the end of the line
    p:last-child
      @apply flex-basis-[10%] flex-grow-1
</style>
